import React from 'react'
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Typography
} from '@mui/material'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Warehouse } from '../../../interfaces/Warehouse/Warehouse'
import { useApi } from '../../../hooks/useApi'
import { createWarehouse, updateWarehouse } from '../config/api'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { ZoneDDL } from '../../../components/Zone'

interface CreateEditWarehouseModalProps {
  warehouse?: Warehouse
  isEditing?: boolean
  closeModal: () => void
  onSuccess: () => void
  handleSnackbar: (message: string, success: boolean) => void
  onRequestStatusChange: (pendingWarehouse: Warehouse) => void
}

const validationSchema = Yup.object().shape({
  warehouseCode: Yup.string()
    .trim()
    .min(1, 'Código de bodega es requerido')
    .max(50, 'Código no puede tener más de 50 caracteres')
    .required('Código de bodega es requerido'),
  warehouseName: Yup.string()
    .trim()
    .min(1, 'Nombre de bodega es requerido')
    .max(50, 'Nombre no puede tener más de 50 caracteres')
    .required('Nombre de bodega es requerido'),
  warehouseAddress: Yup.string()
    .trim()
    .min(1, 'Dirección es requerida')
    .max(100, 'Dirección no puede tener más de 100 caracteres')
    .required('Dirección es requerida'),
  warehouseDescription: Yup.string().max(
    200,
    'Descripción no puede tener más de 200 caracteres'
  ),
  postalCode: Yup.string()
    .max(50, 'Código postal no puede tener más de 50 caracteres')
})

const initialValues: Warehouse = {
  id: 0,
  warehouseCode: '',
  warehouseName: '',
  warehouseAddress: '',
  warehouseDescription: '',
  active: true,
  zoneId: 0,
  zones: [],
  warehouseSchedule: [],
  productOriginWarehouseOperations: [],
  postalCode: ''
}

export const CreateEditWarehouseModal: React.FC<
  CreateEditWarehouseModalProps
> = ({
  warehouse,
  isEditing = false,
  closeModal,
  onSuccess,
  handleSnackbar,
  onRequestStatusChange
}) => {
  const { callApi, state } = useApi()

  const handleSubmit = (
    values: Warehouse,
    { setSubmitting }: FormikHelpers<Warehouse>
  ) => {
    const submitData = {
      warehouseCode: values.warehouseCode?.trim() ?? '',
      warehouseName: values.warehouseName?.trim() ?? '',
      warehouseAddress: values.warehouseAddress?.trim() ?? '',
      warehouseDescription: values.warehouseDescription?.trim() ?? '',
      active: values.active,
      id: values.id,
      postalCode: values.postalCode?.trim() ?? ''
    } as Warehouse

    // Enviar la zona default solo si cambió respecto al valor inicial y es un id válido
    // (-1 es el placeholder "Seleccionar Zona..." de ZoneDDL, no un id real)
    if (values.zoneId !== normalizedInitialValues.zoneId && values.zoneId > 0) {
      submitData.zoneId = values.zoneId
    }

    if (isEditing && warehouse && values.active !== warehouse.active) {
      setSubmitting(false)
      onRequestStatusChange(submitData)
      return
    }

    const apiCall = isEditing
      ? updateWarehouse(values.id, submitData as Warehouse)
      : createWarehouse(submitData as Warehouse)

    callApi(apiCall)
      .then(() => {
        const message = isEditing
          ? 'Bodega actualizada correctamente'
          : 'Bodega creada correctamente'
        handleSnackbar(message, true)
        onSuccess()
        closeModal()
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
  }

  const defaultZoneId =
    warehouse?.zones?.find((z) => z.default)?.zoneId ?? warehouse?.zoneId ?? 0

  const normalizedInitialValues = warehouse
    ? { ...warehouse, zoneId: defaultZoneId }
    : initialValues

  if (state.status === Status.Loading)
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )

  return (
    <Formik
      initialValues={normalizedInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isValid,
        dirty,
        isSubmitting
      }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                name='warehouseCode'
                label='Código'
                fullWidth
                variant='outlined'
                value={values.warehouseCode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.warehouseCode && Boolean(errors.warehouseCode)}
                helperText={touched.warehouseCode && errors.warehouseCode}
                disabled={isSubmitting}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field
                as={TextField}
                name='warehouseName'
                label='Nombre'
                fullWidth
                variant='outlined'
                value={values.warehouseName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.warehouseName && Boolean(errors.warehouseName)}
                helperText={touched.warehouseName && errors.warehouseName}
                disabled={isSubmitting}
                required
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <Field
                as={TextField}
                name='warehouseAddress'
                label='Dirección'
                fullWidth
                variant='outlined'
                value={values.warehouseAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.warehouseAddress && Boolean(errors.warehouseAddress)
                }
                helperText={touched.warehouseAddress && errors.warehouseAddress}
                disabled={isSubmitting}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Field
                as={TextField}
                name='postalCode'
                label='Código Postal'
                fullWidth
                variant='outlined'
                value={values.postalCode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.postalCode && Boolean(errors.postalCode)}
                helperText={touched.postalCode && errors.postalCode}
                disabled={isSubmitting}
              />
            </Grid>

            {isEditing && (
              <Grid item xs={12}>
                <ZoneDDL
                  label='Zona por Defecto'
                  fullWidth
                  variant='outlined'
                  warehouseId={values.id}
                  value={values.zoneId || -1}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Field
                as={TextField}
                name='warehouseDescription'
                label='Descripción'
                fullWidth
                variant='outlined'
                multiline
                rows={3}
                value={values.warehouseDescription}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.warehouseDescription &&
                  Boolean(errors.warehouseDescription)
                }
                helperText={
                  touched.warehouseDescription && errors.warehouseDescription
                }
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Field
                    as={Checkbox}
                    name='active'
                    checked={values.active}
                    onChange={handleChange}
                    color='primary'
                    disabled={isSubmitting}
                  />
                }
                label='Activo'
              />
            </Grid>

            <Grid item xs={12} display='flex' gap={2} justifyContent='flex-end'>
              {closeModal && (
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={closeModal}
                  disabled={isSubmitting}
                  sx={{ borderRadius: '12px' }}
                >
                  Cerrar
                </Button>
              )}
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isValid || !dirty || isSubmitting}
                sx={{ borderRadius: '12px' }}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color='inherit' />
                  ) : undefined
                }
              >
                {isSubmitting
                  ? 'Procesando...'
                  : `Guardar`}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
